import React from "react";
import "./register-guard-log.css";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels, selectLanguage } from "../../slices/language";
import IconifyIcon from "../IconifyIcon";
import { showError } from "../../utils/helpers";
import api, {
  CreateAccessKeyRequest,
  CreateAccessPayload,
  CreateAccessTransactionPhotoPayload,
  CreateAuthorizationPassPayload,
  CreateTransactionPayload,
  SendAuthorizationRequest,
} from "../../api";
import { GateData } from "../../api/models/gate-model";
import { AccessGroupsData } from "../../api/models/access-groups";
import { Button, CircularProgress, Divider, Alert } from "@mui/material";
import { IDetectedBarcode, Scanner } from "@yudiel/react-qr-scanner";
import { selectHasUserPropertySetting, selectUser } from "../../slices/user";
import { AccessGroupCurrent } from "../../routes/Logs/Logs";
import PhotoButton from "../PhotoButton";
import { RequirementTypesData } from "../../api/models/requirement-types";
import dayjs from "dayjs";
import { AccessData } from "../../api/models/access-model";
import ImageModal from "../Modals/ImageModal";
import {
  AccessKeyCreateResponse,
  NormalAccessKeyData,
} from "../../api/models/access-key-model";
import ManualEntranceLog from "../ManualEntranceLog";
import ManualExitLog from "../ManualExitLog";
import AuthorizationRequestSendedModal from "../Modals/AuthorizationRequestSendedModal";
import RequirementsComponent from "../RequirementsComponent";
import PrintablePass, { PrintablePassSnapshot } from "../PrintablePass";
import {
  getAccessTransactionPhotoUrl,
  toAbsoluteMediaUrl,
} from "../../utils/getAccessTransactionPhotoUrl";
import { sortGatesEntranceFirst } from "../../utils/sortGatesEntranceFirst";
import {
  ManualRegistrationScheduleStatus,
  resolveManualRegistrationConfig,
  resolveRegistrationConfig,
} from "../../utils/resolveManualRegistrationConfig";

export interface UnitInfo {
  key: string;
  label: string;
  id: number;
}

export interface UserInfo {
  unit: UnitInfo;
  documentId: string;
  key: string;
  label: string;
  id: number;
}

interface GateInfo {
  id: number;
  name: string;
  type: string;
}

interface RegisterGuardLogProps {
  selectedAccessPoint: number | null;
  accessGroups: AccessGroupsData[] | null;
  exFunction: () => void;
}

const RegisterGuardLog = ({
  selectedAccessPoint,
  accessGroups,
  exFunction,
}: RegisterGuardLogProps) => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectLanguage);
  const hasRegisterAuthorization = useAppSelector(
    selectHasUserPropertySetting("security_guard_transactions"),
  );
  const hasUserAccessGroupsPerms = useAppSelector(
    selectHasUserPropertySetting("user_access_groups"),
  );

  const currentLanguage = language.startsWith("es") ? "es" : "en";

  const propertyId = user?.property?.id;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [registerType, setRegisterType] = React.useState<"manual" | "qr">(
    "manual",
  );
  const [gates, setGates] = React.useState<GateData[] | null>(null);
  const visibleGates = gates?.filter(
    (gate) =>
      registerType !== "manual" ||
      hasRegisterAuthorization ||
      gate.type === "entrance",
  );
  const [step, setStep] = React.useState<"step_1" | "step_2" | "step_3">(
    "step_1",
  );
  const [selectedGate, setSelectedGate] = React.useState<GateInfo | null>(null);
  const [visitorName, setVisitorName] = React.useState<string>("");
  const [unitsArray, setUnitsArray] = React.useState<UnitInfo[]>([]);
  const [unitInputValue, setUnitInputValue] = React.useState<string>("");
  const [unit, setUnit] = React.useState<UnitInfo | null>(null);
  const [usersArray, setUsersArray] = React.useState<UserInfo[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<UserInfo | null>(null);
  const [userInputValue, setUserInputValue] = React.useState<string>("");
  const [motive, setMotive] = React.useState<string>("");
  const [selectedRule, setSelectedRule] =
    React.useState<AccessGroupCurrent | null>(null);
  const [activeRequirements, setActiveRequirements] = React.useState<
    RequirementTypesData[]
  >([]);
  const [scheduleStatus, setScheduleStatus] =
    React.useState<ManualRegistrationScheduleStatus>("allowed");
  const [qrScheduleStatus, setQrScheduleStatus] =
    React.useState<ManualRegistrationScheduleStatus>("allowed");
  const [qrResolvedRule, setQrResolvedRule] =
    React.useState<AccessGroupCurrent | null>(null);
  const [qrActiveRequirements, setQrActiveRequirements] = React.useState<
    RequirementTypesData[]
  >([]);
  const [requirementsValues, setRequirementsValues] = React.useState<any[]>([]);
  const [idFile, setIdFile] = React.useState<File | null>(null);
  const [plateFile, setPlateFile] = React.useState<File | null>(null);
  const [portraitFile, setPortraitFile] = React.useState<File | null>(null);
  const [equipmentFile, setEquipmentFile] = React.useState<File | null>(null);
  const [accessesToExit, setAccessesToExit] = React.useState<
    AccessData[] | null
  >(null);
  const [visitorSearch, setVisitorSearch] = React.useState<string>("");
  const [accessesLoading, setAccessesLoading] = React.useState<boolean>(false);
  const [selectedAccessToExit, setSelectedAccessToExit] =
    React.useState<AccessData | null>(null);
  const [selectedPhoto, setSelectedPhoto] = React.useState<{
    type: string;
    url: string;
  } | null>(null);
  const [showPictureModal, setShowPictureModal] =
    React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>("");
  const [validQr, setValidQr] = React.useState<boolean>(false);
  const [isEntrance, setIsEntrance] = React.useState<boolean>(false);
  const [currentAccessKey, setCurrentAccessKey] =
    React.useState<NormalAccessKeyData | null>(null);
  const [qrStatusValue, setQrStatusValue] = React.useState<{
    message: {
      en: string;
      es: string;
    };
    type: "entrance" | "exit" | "try";
  } | null>(null);
  const [countryCode, setCountryCode] = React.useState<"+52" | "+1">("+52");
  const [visitorPhone, setVisitorPhone] = React.useState<string>("");
  const [sendSuccessModal, setShowSendSuccessModal] =
    React.useState<boolean>(false);
  const [printablePassOpen, setPrintablePassOpen] =
    React.useState<boolean>(false);
  const [printablePassData, setPrintablePassData] =
    React.useState<PrintablePassSnapshot | null>(null);
  const [userQrId, setUserQrId] = React.useState<number | null>(null);
  const [userQrUnit, setUserQrUnit] = React.useState<number | null>(null);
  const [cameraStatus, setCameraStatus] = React.useState<
    "idle" | "checking" | "available" | "unavailable"
  >("idle");

  const debouncedVisitor = useDebounce(visitorSearch, 300);

  const constraints = React.useMemo<MediaTrackConstraints>(
    () => ({
      aspectRatio: 1,
      facingMode: { ideal: "environment" },
    }),
    [],
  );

  React.useEffect(() => {
    setStep("step_1");
    setScheduleStatus("allowed");
    setSelectedRule(null);
    setActiveRequirements([]);
  }, [selectedAccessPoint]);

  const applyManualRegistrationConfig = React.useCallback(
    (gateType: "entrance" | "exit") => {
      const resolved = resolveManualRegistrationConfig(
        accessGroups,
        selectedAccessPoint,
        gateType,
      );
      setScheduleStatus(resolved.status);
      setSelectedRule(resolved.rule);
      setActiveRequirements(resolved.requirements);
      setRequirementsValues([]);
    },
    [accessGroups, selectedAccessPoint],
  );

  const manualScheduleAllowed = scheduleStatus === "allowed";

  const scheduleWarningMessage =
    scheduleStatus === "exit_not_allowed"
      ? labels.manual_exit_not_allowed_schedule
      : scheduleStatus === "entrance_not_allowed"
        ? labels.manual_entrance_not_allowed_schedule
        : "";

  React.useEffect(() => {
    if (
      registerType === "manual" &&
      step === "step_2" &&
      selectedGate?.type &&
      (selectedGate.type === "entrance" || selectedGate.type === "exit")
    ) {
      applyManualRegistrationConfig(selectedGate.type);
    }
  }, [
    accessGroups,
    applyManualRegistrationConfig,
    registerType,
    selectedAccessPoint,
    selectedGate?.type,
    step,
  ]);

  React.useEffect(() => {
    if (step !== "step_2" || registerType !== "qr") {
      setCameraStatus("idle");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus("unavailable");
      return;
    }
    setCameraStatus("checking");
    navigator.mediaDevices
      .getUserMedia({ video: constraints })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        setCameraStatus("available");
      })
      .catch(() => setCameraStatus("unavailable"));
  }, [step, registerType, constraints]);

  const showLogButton =
    registerType === "manual"
      ? step === "step_2" && manualScheduleAllowed
        ? true
        : false
      : step === "step_3"
        ? true
        : false;

  const defaultUnitsProps = {
    options: unitsArray ? unitsArray : [],
    getOptionLabel: (option: UnitInfo) => option.label,
  };

  const defaultUsersProps = {
    options: unit ? usersArray.filter((item) => item.unit.id === unit.id) : usersArray,
    getOptionLabel: (option: UserInfo) => option.label,
  };

  const photos: { type: string; url: string }[] = React.useMemo(() => {
    return (
      selectedAccessToExit?.entrance_transaction?.access_transaction_photos?.map(
        (item) => {
          return {
            type: item?.type, //photo id | plates | visitor | equipment
            url: getAccessTransactionPhotoUrl(item),
          };
        },
      ) || []
    );
  }, [selectedAccessToExit?.entrance_transaction?.access_transaction_photos]);

  const accessKeyPhotos =
    validQr && qrScheduleStatus === "allowed"
      ? (qrResolvedRule?.photographs ?? [])
      : [];
  const manualPhotos = selectedRule?.photographs ?? [];

  type PhotoKey = "id" | "plates" | "visitor" | "other";

  const fileMap: Record<PhotoKey, any> = {
    id: idFile,
    plates: plateFile,
    visitor: portraitFile,
    other: equipmentFile,
  };

  const qrRequirementTypes = React.useMemo(
    (): RequirementTypesData[] =>
      validQr && qrScheduleStatus === "allowed" ? qrActiveRequirements : [],
    [validQr, qrScheduleStatus, qrActiveRequirements],
  );

  const qrScheduleAllowed = qrScheduleStatus === "allowed";

  const qrScheduleWarningMessage =
    qrScheduleStatus === "exit_not_allowed"
      ? labels.manual_exit_not_allowed_schedule
      : qrScheduleStatus === "entrance_not_allowed"
        ? labels.manual_entrance_not_allowed_schedule
        : "";

  const qrRequiredRequirementsFilled = React.useMemo(() => {
    const reqs = qrRequirementTypes;
    const requiredIndices = reqs
      .map((req, index) => (req?.required ? index : -1))
      .filter((i) => i >= 0);
    if (requiredIndices.length === 0) return true;
    return requiredIndices.every((index) => {
      const val = requirementsValues[index]?.data?.value;
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    });
  }, [qrRequirementTypes, requirementsValues]);

  const disabledQrEntranceLog =
    !qrScheduleAllowed ||
    accessKeyPhotos?.some((key) => !fileMap[key as PhotoKey]) ||
    (qrRequirementTypes.length > 0 && !qrRequiredRequirementsFilled);

  const disabledQrExitLog = !qrScheduleAllowed;

  const requiredRequirementsFilled = React.useMemo(() => {
    const reqs = activeRequirements;
    const requiredIndices = reqs
      .map((req, index) => (req?.required ? index : -1))
      .filter((i) => i >= 0);
    if (requiredIndices.length === 0) return true;
    return requiredIndices.every((index) => {
      const val = requirementsValues[index]?.data?.value;
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    });
  }, [activeRequirements, requirementsValues]);

  const disabledManualLog = hasRegisterAuthorization
    ? manualPhotos?.some(
        (key) => !fileMap[key as PhotoKey] || !selectedUser || !unit,
      ) || !requiredRequirementsFilled
    : !visitorName || !visitorPhone || !selectedUser?.documentId || !unit || !motive;

  const ruleForPrintablePass = React.useMemo((): AccessGroupCurrent | null => {
    if (registerType === "qr") return qrResolvedRule;
    return selectedRule;
  }, [registerType, qrResolvedRule, selectedRule]);

  React.useEffect(() => {
    if (photos && photos.length > 0) {
      const photoId =
        photos.filter((item) => item?.type === "photo id")[0] || null;
      const photoPlates =
        photos.filter((item) => item?.type === "plates")[0] || null;
      const photoVisitor =
        photos.filter((item) => item?.type === "visitor")[0] || null;
      const photoEquipment =
        photos.filter((item) => item?.type === "equipment")[0] || null;

      if (photoId) {
        setSelectedPhoto(photoId);
      } else if (photoPlates) {
        setSelectedPhoto(photoPlates);
      } else if (photoVisitor) {
        setSelectedPhoto(photoVisitor);
      } else if (photoEquipment) {
        setSelectedPhoto(photoEquipment);
      }
    }
  }, [photos]);

  const defaultManual = () => {
    setRequirementsValues([]);
    setScheduleStatus("allowed");
    setSelectedRule(null);
    setActiveRequirements([]);
    setQrScheduleStatus("allowed");
    setQrResolvedRule(null);
    setQrActiveRequirements([]);
    setIdFile(null);
    setPlateFile(null);
    setEquipmentFile(null);
    setPortraitFile(null);
    setVisitorName("");
    setUnit(null);
    setUnitInputValue("");
    setSelectedUser(null);
    setUserInputValue("");
    setMotive("");
    setVisitorSearch("");
    setSelectedPhoto(null);
    setSelectedAccessToExit(null);
    setMessage("");
    setValidQr(false);
    setCurrentAccessKey(null);
    setIsEntrance(false);
    setQrStatusValue(null);
    setCountryCode("+52");
    setVisitorPhone("");
  };

  const applyQrRegistrationConfig = (
    accessKey: NormalAccessKeyData | null,
    isValid: boolean,
    gateType: "entrance" | "exit" | undefined,
  ) => {
    if (!isValid || !accessKey || !gateType || !selectedAccessPoint) {
      setQrScheduleStatus("allowed");
      setQrResolvedRule(null);
      setQrActiveRequirements([]);
      return;
    }

    if (accessKey.access_key_type === "authorization") {
      setQrScheduleStatus("allowed");
      setQrResolvedRule(null);
      setQrActiveRequirements([]);
      return;
    }

    const resolved = resolveRegistrationConfig(
      accessKey.access_groups ?? [],
      selectedAccessPoint,
      gateType,
      new Date(),
      { onlyVisitorGroups: false },
    );
    setQrScheduleStatus(resolved.status);
    setQrResolvedRule(resolved.rule);
    setQrActiveRequirements(resolved.requirements);
  };

  const changeRegisterType = (type: "manual" | "qr") => {
    setRegisterType(type);
    setStep("step_1");
    defaultManual();
  };

  const onReadCode = async (result: IDetectedBarcode[]) => {
    const resultUuid = result[0]?.rawValue;
    if (resultUuid && selectedGate?.id && propertyId) {
      try {
        const validateResponse = await api.validateKey({
          gate_id: selectedGate.id,
          key: resultUuid,
        });
        if (validateResponse) {
          let accessKeyVerifiable = "";
          const accessKeyByIdentifier = await api.getInvitationByIdentifier2(
            resultUuid,
            propertyId,
          );
          if (accessKeyByIdentifier.length > 0) {
            const activeAccessKey = accessKeyByIdentifier.find(
              (item) => item.daleted_at === null,
            );
            if (activeAccessKey) {
              accessKeyVerifiable = activeAccessKey?.verifiable_ids[0]?.uuid;
            } else {
              const deactiveAccessKey = accessKeyByIdentifier.find(
                (item) => item.daleted_at !== null,
              );
              if (deactiveAccessKey) {
                accessKeyVerifiable =
                  deactiveAccessKey?.verifiable_ids[0]?.uuid;
              }
            }
          } else {
            accessKeyVerifiable = resultUuid;
          }
          const accessKeyByVerifiable =
            await api.verifiableIdByUuidRequest(accessKeyVerifiable);

          const accessKeyData =
            accessKeyByVerifiable && accessKeyByVerifiable?.data.length > 0
              ? accessKeyByVerifiable?.data[0]?.access_key_qr
              : null;

          if (accessKeyData) {
            setCurrentAccessKey(accessKeyData);
            setRequirementsValues([]);
          }
          let currentStatus: {
            message: {
              en: string;
              es: string;
            };
            type: "entrance" | "exit" | "try";
          } | null = null;
          if (
            validateResponse?.message?.en === "Exit authorized." ||
            validateResponse?.message?.en === "Entrance authorized." ||
            validateResponse?.message?.en === "Access authorized.." ||
            validateResponse?.message?.en === "Exit authorized.."
          ) {
            if (
              validateResponse?.message?.en === "Entrance authorized." ||
              validateResponse?.message?.en === "Access authorized.."
            ) {
              setIsEntrance(true);
              currentStatus = {
                message: {
                  en: validateResponse?.message?.en,
                  es: validateResponse?.message?.es,
                },
                type: "entrance",
              };
            }
            if (
              validateResponse?.message?.en === "Exit authorized." ||
              validateResponse?.message?.en === "Exit authorized.."
            ) {
              setIsEntrance(false);
              currentStatus = {
                message: {
                  en: validateResponse?.message?.en,
                  es: validateResponse?.message?.es,
                },
                type: "exit",
              };
            }
            setValidQr(true);
          } else {
            // Acceso NO autorizado
            currentStatus = {
              message: {
                en: validateResponse?.message?.en,
                es: validateResponse?.message?.es,
              },
              type: "try",
            };
            setValidQr(false);
            applyQrRegistrationConfig(null, false, undefined);
            if (hasUserAccessGroupsPerms) {
              const userByUuid = await getAccessKeyByUuid(resultUuid);
              const userByIdentifier =
                await getAccessKeyByIdentifier(resultUuid);
              if (userByUuid || userByIdentifier) {
                if (userByUuid) {
                  setUserQrId(userByUuid?.userId);
                  setUserQrUnit(userByUuid?.unitId);
                } else if (userByIdentifier) {
                  setUserQrId(userByIdentifier?.userId);
                  setUserQrUnit(userByIdentifier?.unitId);
                } else {
                  setUserQrId(null);
                  setUserQrUnit(null);
                }
              } else {
                setUserQrId(null);
                setUserQrUnit(null);
              }
            }
          }
          const gateType: "entrance" | "exit" | undefined =
            selectedGate?.type === "exit"
              ? "exit"
              : selectedGate?.type === "entrance"
                ? "entrance"
                : undefined;
          applyQrRegistrationConfig(
            accessKeyData ?? null,
            !!(
              validateResponse?.message?.en === "Exit authorized." ||
              validateResponse?.message?.en === "Entrance authorized." ||
              validateResponse?.message?.en === "Access authorized.." ||
              validateResponse?.message?.en === "Exit authorized.."
            ),
            gateType,
          );
          setQrStatusValue(currentStatus);
          setMessage(validateResponse?.message[currentLanguage]);
          setStep("step_3");
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const getAccessPointData = React.useCallback(async () => {
    const selectedAccessPointDocumentId = user?.access_points?.find(
      (item) => item?.id === selectedAccessPoint,
    )?.documentId;
    if (selectedAccessPoint && selectedAccessPointDocumentId) {
      try {
        setIsLoading(true);
        const response = await api.accessPointByIdRequest(
          selectedAccessPointDocumentId,
        );
        if (response) {
          const fixedResponse = response.data;
          const resGates = fixedResponse?.gates || null;
          setGates(sortGatesEntranceFirst(resGates));
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setGates(null);
    }
  }, [
    dispatch,
    labels.generic_error,
    selectedAccessPoint,
    user?.access_points,
  ]);

  React.useEffect(() => {
    getAccessPointData();
  }, [getAccessPointData]);

  const getUnits = React.useCallback(async () => {
    if (propertyId && registerType === "manual" && step === "step_2") {
      try {
        setIsLoading(true);
        const response = await api.unitsRequest(Number(propertyId), 1, 100);
        if (response) {
          const array: UnitInfo[] = response.data.map((item, index) => {
            return {
              key: `${item?.id}-${index}`,
              label: item.name,
              id: item?.id,
            };
          });
          setUnitsArray(array);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [dispatch, labels.generic_error, propertyId, registerType, step]);

  React.useEffect(() => {
    getUnits();
  }, [getUnits]);

  React.useEffect(() => {
    let active = true;
    setUsersArray([]);
    setSelectedUser(null);
    setUserInputValue("");
    setUnit(null);
    setUnitInputValue("");
    if (!propertyId || registerType !== "manual" || step !== "step_2") return;
    api.manualRegistrationUsersRequest(propertyId)
      .then((users) => {
        if (!active) return;
        setUsersArray(users.filter((item) => item.unit?.id).map((item) => ({
          key: `${item.id}`,
          id: item.id,
          documentId: item.documentId,
          label: item.name || item.username,
          unit: {
            key: `${item.unit!.id}`,
            id: item.unit!.id,
            label: item.unit!.name,
          },
        })));
      })
      .catch((error) => {
        if (active) showError(dispatch, error?.response?.data?.error?.message || labels.generic_error);
      });
    return () => { active = false; };
  }, [propertyId, registerType, step, dispatch, labels.generic_error]);

  const getEntraceTransactions = React.useCallback(
    async (debouncedText: string) => {
      if (propertyId) {
        try {
          setAccessesLoading(true);
          const response = await api.getNoExitAccessesRequest(
            propertyId,
            debouncedText,
          );
          if (response) {
            setAccessesToExit(response?.data);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setAccessesLoading(false);
        }
      }
    },
    [dispatch, labels.generic_error, propertyId],
  );

  React.useEffect(() => {
    if (
      selectedGate?.type === "exit" &&
      step === "step_2" &&
      registerType === "manual"
    ) {
      getEntraceTransactions(debouncedVisitor);
    }
  }, [
    debouncedVisitor,
    getEntraceTransactions,
    registerType,
    selectedGate?.type,
    step,
  ]);

  const qrLog = async () => {
    if (currentAccessKey && selectedGate && selectedAccessPoint) {
      const doorType = selectedGate.type; //entrance or exit
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      try {
        setIsLoading(true);
        const transactionPhotosIds: number[] = [];
        const currentDateTime = dayjs().toISOString();
        const visitor = currentAccessKey?.one_time_visitor?.provider?.name
          ? currentAccessKey?.one_time_visitor?.provider?.name
          : currentAccessKey?.one_time_visitor?.provider_name
            ? currentAccessKey?.one_time_visitor?.provider_name
            : currentAccessKey?.one_time_visitor?.name
              ? currentAccessKey?.one_time_visitor?.name
              : currentAccessKey?.verified_visitor?.name
                ? currentAccessKey?.verified_visitor?.name
                : "";
        const requiredPhotos = qrResolvedRule?.photographs ?? [];
        let accessPayload: CreateAccessPayload = {
          data: {
            property: user?.property?.id,
            company: user?.company?.id,
            user: currentAccessKey?.created?.name,
            unit: currentAccessKey?.unit?.name,
            visitor: visitor,
            authorized: validQr,
            access_key_id: currentAccessKey?.id,
          },
        };
        let transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: doorType === "entrance" ? "entrance" : "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPoint,
            access_key_id: `${currentAccessKey?.id}`,
            users_permissions_users: guardIds,
            access_key_type: currentAccessKey?.access_key_type,
            status: qrStatusValue ? qrStatusValue : null,
          },
        };
        if (requiredPhotos.length > 0) {
          const transactionPhotosPayload: CreateAccessTransactionPhotoPayload[] =
            requiredPhotos.map((item) => {
              return {
                data: {
                  type:
                    item === "id"
                      ? "photo id"
                      : item === "plates"
                        ? "plates"
                        : item === "visitor"
                          ? "visitor"
                          : "equipment",
                },
              };
            });
          // Procesar cada transactionPhotosPayload
          for (const photoPayload of transactionPhotosPayload) {
            // Crear la transacción de foto
            const response =
              await api.createAccessTransactionPhoto(photoPayload);
            console.log("response", response);

            // Subir la foto asociada
            if (response?.data?.id) {
              transactionPhotosIds.push(response?.data?.id);
              const file =
                photoPayload?.data?.type === "photo id"
                  ? idFile
                  : photoPayload?.data?.type === "plates"
                    ? plateFile
                    : photoPayload?.data?.type === "equipment"
                      ? equipmentFile
                      : photoPayload?.data?.type === "visitor"
                        ? portraitFile
                        : "";
              if (file) {
                await api.uploadMediaRequest({
                  files: [file],
                  ref: "api::access-transaction-photo.access-transaction-photo",
                  refId: response?.data?.id,
                  field: "image",
                });
              } else {
                console.warn(
                  `No file provided for photo type: ${photoPayload?.data?.photo_type}`,
                );
              }
            }
          }
          transactionPayload = {
            data: {
              ...transactionPayload.data,
              access_transaction_photos: transactionPhotosIds,
            },
          };
        }
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          const transactionId = transactionResponse?.id;
          const transactionDocId = transactionResponse?.documentId;
          if (transactionId && requirementsValues?.length > 0) {
            for (const requ of requirementsValues) {
              const val = requ?.data?.value;
              if (val !== undefined && val !== null && String(val).trim() !== "") {
                await api.createRequirement({
                  data: {
                    requirement_type: requ?.data?.requirement_type,
                    access_transaction: { connect: [transactionDocId] },
                    value: String(val),
                  },
                });
              }
            }
          }
          if (doorType === "entrance") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                entrance_transaction: transactionId,
              },
            };
          }
          if (doorType === "exit") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                exit_transaction: transactionId,
              },
            };
          }
          if (currentAccessKey?.access_key_type === "authorization") {
            const accerssKeyPayload: CreateAccessKeyRequest = {
              daleted_at: currentDateTime,
            };
            await api.updateAccessKeyRequest(
              currentAccessKey?.id,
              accerssKeyPayload,
            );
          }
          const accessResponse = await api.createAccess(accessPayload);
          if (accessResponse) {
            exFunction();
            const showPrintablePassFlow =
              doorType === "entrance" &&
              validQr === true &&
              ruleForPrintablePass?.printable_pass === true;
            if (showPrintablePassFlow) {
              const createdAttrs = currentAccessKey?.created;
              const receiveName =
                (createdAttrs?.name && String(createdAttrs.name).trim()) ||
                createdAttrs?.username ||
                "";
              setPrintablePassData({
                visitorName: visitor,
                receiveName,
                department: currentAccessKey?.unit?.name ?? "",
                registeredAt: currentDateTime,
                accessType: "entrance",
              });
              setPrintablePassOpen(true);
            } else {
              setStep("step_1");
              defaultManual();
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const exitQrLog = async () => {
    if (currentAccessKey && selectedGate && selectedAccessPoint && propertyId) {
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      const doorType = selectedGate.type; //entrance or exit
      if (selectedGate.type === "exit") {
        if (!validQr) {
          try {
            const visitor = currentAccessKey?.one_time_visitor?.provider?.name
              ? currentAccessKey?.one_time_visitor?.provider?.name
              : currentAccessKey?.one_time_visitor?.provider_name
                ? currentAccessKey?.one_time_visitor?.provider_name
                : currentAccessKey?.one_time_visitor?.name
                  ? currentAccessKey?.one_time_visitor?.name
                  : currentAccessKey?.verified_visitor?.name
                    ? currentAccessKey?.verified_visitor?.name
                    : "";
            setIsLoading(true);
            const currentDateTime = dayjs().toISOString();
            let transactionPayload: CreateTransactionPayload = {
              data: {
                datetime: currentDateTime,
                access_type: doorType === "entrance" ? "entrance" : "exit",
                gate: selectedGate?.id,
                // access_point: selectedAccessPoint,
                access_key_id: `${currentAccessKey?.id}`,
                users_permissions_users: guardIds,
                access_key_type: currentAccessKey?.access_key_type,
                status: qrStatusValue ? qrStatusValue : null,
              },
            };
            const transactionResponse =
              await api.createTransaction(transactionPayload);
            if (transactionResponse) {
              const transactionId = transactionResponse?.id;
              // if (
              //   qrRequirementTypes.length > 0 &&
              //   transactionId &&
              //   requirementsValues?.length > 0
              // ) {
              //   for (let i = 0; i < qrRequirementTypes.length; i++) {
              //     const val = requirementsValues[i]?.data?.value;
              //     if (
              //       val !== undefined &&
              //       val !== null &&
              //       String(val).trim() !== ""
              //     ) {
              //       await api.createRequirement({
              //         data: {
              //           requirement_type: qrRequirementTypes[i]?.id,
              //           access_key_qr: currentAccessKey?.id,
              //           access_transaction: transactionId,
              //           value: String(val),
              //         },
              //       });
              //     }
              //   }
              // }
              const accessCreatePayload: CreateAccessPayload = {
                data: {
                  property: user?.property?.id,
                  company: user?.company?.id,
                  user: currentAccessKey?.created?.name,
                  unit: currentAccessKey?.unit?.name,
                  visitor: visitor,
                  authorized: validQr,
                  access_key_id: currentAccessKey?.id,
                  exit_transaction: transactionId,
                },
              };
              const createAccess = await api.createAccess(accessCreatePayload);
              if (createAccess) {
                exFunction();
                setStep("step_1");
                defaultManual();
              }
            }
          } catch (error: any) {
            console.error(error);
            const errorMessage = error?.response?.data?.error?.message;
            showError(
              dispatch,
              errorMessage ? errorMessage : labels.generic_error,
            );
          } finally {
            setIsLoading(false);
          }
        } else {
          const visitor = currentAccessKey?.one_time_visitor?.provider?.name
            ? currentAccessKey?.one_time_visitor?.provider?.name
            : currentAccessKey?.one_time_visitor?.provider_name
              ? currentAccessKey?.one_time_visitor?.provider_name
              : currentAccessKey?.one_time_visitor?.name
                ? currentAccessKey?.one_time_visitor?.name
                : currentAccessKey?.verified_visitor?.name
                  ? currentAccessKey?.verified_visitor?.name
                  : "";
          try {
            setIsLoading(true);
            const currentDateTime = dayjs().toISOString();
            const getAccess = await api.getAccessesByAccessKeyRequest(
              propertyId,
              currentAccessKey?.id,
            );
            let transactionPayload: CreateTransactionPayload = {
              data: {
                datetime: currentDateTime,
                access_type: doorType === "entrance" ? "entrance" : "exit",
                gate: selectedGate?.id,
                // access_point: selectedAccessPoint,
                access_key_id: `${currentAccessKey?.id}`,
                users_permissions_users: guardIds,
                access_key_type: currentAccessKey?.access_key_type,
                status: qrStatusValue ? qrStatusValue : null,
              },
            };
            const transactionResponse =
              await api.createTransaction(transactionPayload);
            if (transactionResponse) {
              const transactionId = transactionResponse?.id;
              const transactionDocId = transactionResponse?.documentId;
              if (
                qrRequirementTypes.length > 0 &&
                transactionId &&
                requirementsValues?.length > 0
              ) {
                for (let i = 0; i < qrRequirementTypes.length; i++) {
                  const val = requirementsValues[i]?.data?.value;
                  if (
                    val !== undefined &&
                    val !== null &&
                    String(val).trim() !== ""
                  ) {
                    await api.createRequirement({
                      data: {
                        requirement_type: qrRequirementTypes[i]?.id,
                        access_key_qr: currentAccessKey?.id,
                        access_transaction: { connect: [transactionDocId] },
                        value: String(val),
                      },
                    });
                  }
                }
              }
              if (currentAccessKey?.access_key_type === "authorization") {
                const accerssKeyPayload: CreateAccessKeyRequest = {
                  daleted_at: currentDateTime,
                };
                await api.updateAccessKeyRequest(
                  currentAccessKey?.id,
                  accerssKeyPayload,
                );
              }
              if (getAccess?.data && getAccess?.data?.length > 0) {
                const accessPayload: CreateAccessPayload = {
                  data: {
                    exit_transaction: transactionId,
                  },
                };
                const updateAccess = await api.updateAccess(
                  getAccess?.data[0]?.documentId,
                  accessPayload,
                );
                if (updateAccess) {
                  exFunction();
                  setStep("step_1");
                  defaultManual();
                }
              } else {
                const accessCreatePayload: CreateAccessPayload = {
                  data: {
                    property: user?.property?.id,
                    company: user?.company?.id,
                    user: currentAccessKey?.created?.name,
                    unit: currentAccessKey?.unit?.name,
                    visitor: visitor,
                    authorized: validQr,
                    access_key_id: currentAccessKey?.id,
                    exit_transaction: transactionId,
                  },
                };
                const createAccess =
                  await api.createAccess(accessCreatePayload);
                if (createAccess) {
                  exFunction();
                  setStep("step_1");
                  defaultManual();
                }
              }
            }
          } catch (error: any) {
            console.error(error);
            const errorMessage = error?.response?.data?.error?.message;
            showError(
              dispatch,
              errorMessage ? errorMessage : labels.generic_error,
            );
          } finally {
            setIsLoading(false);
          }
        }
      }
    }
  };

  const sendAuthRequest = async () => {
    if (userQrId && userQrUnit && selectedAccessPoint) {
      try {
        setIsLoading(true);
        const createAuthorizationPassPayload: CreateAuthorizationPassPayload = {
          data: {
            user: userQrId,
            unit: userQrUnit,
            access_point: selectedAccessPoint,
            type: selectedGate?.type === "entrance" ? "entrance" : "exit",
          },
        };
        const createAuthorizationPassResponse =
          await api.createAuthorizationPass(createAuthorizationPassPayload);
        if (createAuthorizationPassResponse) {
          const sendAuthPayload: SendAuthorizationRequest = {
            pass_id: createAuthorizationPassResponse.documentId,
          };
          const sendAuthResponse =
            await api.sendAuthorizationRequest(sendAuthPayload);
          if (sendAuthResponse) {
            if (sendAuthResponse?.success) {
              setShowSendSuccessModal(true);
            } else {
              showError(dispatch, labels.authorization_sent_error);
            }
          } else {
            showError(dispatch, labels.authorization_sent_error);
          }
        } else {
          showError(dispatch, labels.authorization_sent_error);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const manualLog = async () => {
    if (!selectedGate || !selectedGate.type || !selectedAccessPoint) return;

    const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
    if (!hasRegisterAuthorization) {
      if (!selectedUser?.documentId) return;
      try {
        setIsLoading(true);
        const createAuthorizationPassPayload: CreateAuthorizationPassPayload = {
          data: {
            visitor_name: visitorName,
            visitor_phone: `${countryCode}${visitorPhone}`,
            unit: unit?.id,
            access_point: selectedAccessPoint,
            type: "visitor",
          },
        };
        const createAuthorizationPassResponse =
          await api.createAuthorizationPass(createAuthorizationPassPayload);
        if (createAuthorizationPassResponse) {
          const sendAuthPayload: SendAuthorizationRequest = {
            pass_id: createAuthorizationPassResponse.documentId,
            receiver_docid: selectedUser.documentId,
          };
          const sendAuthResponse =
            await api.sendAuthorizationRequest(sendAuthPayload);
          if (sendAuthResponse) {
            if (sendAuthResponse?.success) {
              setShowSendSuccessModal(true);
            } else {
              showError(dispatch, labels.authorization_sent_error);
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    } else {
      const doorType = selectedGate.type; //entrance or exit
      try {
        setIsLoading(true);
        const transactionPhotosIds: number[] = [];
        const currentDateTime = dayjs().toISOString();
        let accessPayload: CreateAccessPayload = {
          data: {
            property: user?.property?.id,
            company: user?.company?.id,
            user: selectedUser?.label,
            unit: unit?.label,
            visitor: visitorName,
            authorized: true,
          },
        };
        let transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: doorType === "entrance" ? "entrance" : "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPoint,
            users_permissions_users: guardIds,
          },
        };
        if (selectedRule?.photographs) {
          const transactionPhotosPayload: CreateAccessTransactionPhotoPayload[] =
            selectedRule?.photographs.map((item) => {
              return {
                data: {
                  type:
                    item === "id"
                      ? "photo id"
                      : item === "plates"
                        ? "plates"
                        : item === "visitor"
                          ? "visitor"
                          : "equipment",
                },
              };
            });
          // Procesar cada transactionPhotosPayload
          for (const photoPayload of transactionPhotosPayload) {
            // Crear la transacción de foto
            const response =
              await api.createAccessTransactionPhoto(photoPayload);

            // Subir la foto asociada
            if (response?.data?.id) {
              transactionPhotosIds.push(response?.data?.id);
              const file =
                photoPayload?.data?.type === "photo id"
                  ? idFile
                  : photoPayload?.data?.type === "plates"
                    ? plateFile
                    : photoPayload?.data?.type === "equipment"
                      ? equipmentFile
                      : photoPayload?.data?.type === "visitor"
                        ? portraitFile
                        : "";
              if (file) {
                await api.uploadMediaRequest({
                  files: [file],
                  ref: "api::access-transaction-photo.access-transaction-photo",
                  refId: response?.data?.id,
                  field: "image",
                });
              } else {
                console.warn(
                  `No file provided for photo type: ${photoPayload?.data?.photo_type}`,
                );
              }
            }
          }
          transactionPayload = {
            data: {
              ...transactionPayload.data,
              access_transaction_photos: transactionPhotosIds,
            },
          };
        }
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          console.log("transactionResponse", transactionResponse);
          const transactionId = transactionResponse?.id;
          const transactionDocId = transactionResponse?.documentId;
          if (doorType === "entrance") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                entrance_transaction: transactionResponse?.id,
              },
            };
          }
          if (doorType === "exit") {
            accessPayload = {
              data: {
                ...accessPayload.data,
                exit_transaction: transactionResponse?.id,
              },
            };
          }
          if (transactionId && requirementsValues?.length > 0) {
            console.log("requirementsValues", requirementsValues);
            for (const requ of requirementsValues) {
              const val = requ?.data?.value;
              if (val !== undefined && val !== null && String(val).trim() !== "") {
                await api.createRequirement({
                  data: {
                    requirement_type: requ?.data?.requirement_type,
                    access_transaction: { connect: [transactionDocId] },
                    value: String(val),
                  },
                });
              }
            }
          }
          const accessResponse = await api.createAccess(accessPayload);
          if (accessResponse) {
            exFunction();
            const showPrintablePassFlow =
              doorType === "entrance" &&
              ruleForPrintablePass?.printable_pass === true;
            if (showPrintablePassFlow) {
              setPrintablePassData({
                visitorName,
                receiveName: selectedUser?.label ?? "",
                department: unit?.label ?? "",
                registeredAt: currentDateTime,
                accessType: "entrance",
              });
              setPrintablePassOpen(true);
            } else {
              setStep("step_1");
              defaultManual();
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const finishPrintablePassFlow = React.useCallback(() => {
    setPrintablePassOpen(false);
    setPrintablePassData(null);
    setStep("step_1");
    defaultManual();
  }, []);

  const onLogExit = async () => {
    if (selectedAccessToExit && selectedGate && selectedAccessPoint) {
      const guardIds: number[] | undefined = user?.id ? [user?.id] : undefined;
      try {
        setIsLoading(true);
        const currentDateTime = dayjs().toISOString();
        const currentAccessId = selectedAccessToExit?.documentId;
        const transactionPayload: CreateTransactionPayload = {
          data: {
            datetime: currentDateTime,
            access_type: "exit",
            gate: selectedGate?.id,
            // access_point: selectedAccessPoint,
            users_permissions_users: guardIds,
          },
        };
        const transactionResponse =
          await api.createTransaction(transactionPayload);
        if (transactionResponse) {
          const accessPayload: CreateAccessPayload = {
            data: {
              exit_transaction: transactionResponse?.id,
            },
          };
          const updateAccess = await api.updateAccess(
            currentAccessId,
            accessPayload,
          );
          if (updateAccess) {
            exFunction();
            setStep("step_1");
            defaultManual();
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getAccessKeyByUuid = async (uuid: string) => {
    try {
      const accessKeyResponse = await api.verifiableIdByUuidRequest(uuid);
      if (accessKeyResponse && accessKeyResponse?.data?.length > 0) {
        const qrAccessKeyResponse = accessKeyResponse?.data[0]?.access_key_qr;
        const accessKeyType = qrAccessKeyResponse?.access_key_type;
        if (accessKeyType === "user") {
          const accessKeyUserId = qrAccessKeyResponse?.verified_visitor?.id;
          const accessKeyUnitId = qrAccessKeyResponse?.unit?.id;
          if (accessKeyUserId && accessKeyUnitId) {
            return { userId: accessKeyUserId, unitId: accessKeyUnitId };
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const getAccessKeyByIdentifier = async (identifier: string) => {
    if (!propertyId) return null;
    try {
      const accessKeyResponse = await api.getInvitationByIdentifier(
        identifier,
        propertyId,
      );
      if (accessKeyResponse && accessKeyResponse?.length > 0) {
        const qrAccessKeyResponse: AccessKeyCreateResponse =
          accessKeyResponse[0];
        const accessKeyType = qrAccessKeyResponse?.access_key_type;
        if (accessKeyType === "user") {
          const accessKeyUserId = qrAccessKeyResponse?.verified_visitor?.id;
          const accessKeyUnitId = qrAccessKeyResponse?.unit?.id;
          if (accessKeyUserId && accessKeyUnitId) {
            return { userId: accessKeyUserId, unitId: accessKeyUnitId };
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const imageToShow = React.useMemo(
    () => toAbsoluteMediaUrl(selectedPhoto?.url),
    [selectedPhoto],
  );

  return (
    <div className="w-100 h-100 border-box">
      <AuthorizationRequestSendedModal
        open={sendSuccessModal}
        setOpen={setShowSendSuccessModal}
        onClose={() => {
          setUserQrId(null);
          setUserQrUnit(null);
          setStep("step_1");
          defaultManual();
        }}
      />
      <PrintablePass
        open={printablePassOpen}
        setOpen={setPrintablePassOpen}
        data={printablePassData}
        onFinished={finishPrintablePassFlow}
      />
      <ImageModal
        showModal={showPictureModal}
        setShowModal={setShowPictureModal}
        // srcImage={`${selectedPhoto?.url}`}
        srcImage={imageToShow}
        imageType={`${selectedPhoto?.type}`}
        visitorName={`${selectedAccessToExit?.visitor}`}
      />
      <div
        className="w-100 border-box flex align-items-center px-4"
        style={{
          height: 50,
          borderBottom: "1px #e0e0e0 solid",
          backgroundColor: "white",
          zIndex: 99,
        }}
      >
        <div className="register-type-switch" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={registerType === "manual"}
            className={`register-type-option ${
              registerType === "manual" ? "register-type-option-selected" : ""
            }`}
            onClick={() => {
              if (registerType !== "manual") changeRegisterType("manual");
            }}
          >
            <IconifyIcon icon={"solar:clipboard-bold"} fontSize={18} />
            <span>{labels.manual_log}</span>
            {registerType === "manual" && (
              <IconifyIcon
                icon={"material-symbols:check-rounded"}
                fontSize={18}
              />
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={registerType === "qr"}
            className={`register-type-option ${
              registerType === "qr" ? "register-type-option-selected" : ""
            }`}
            onClick={() => {
              if (registerType !== "qr") changeRegisterType("qr");
            }}
          >
            <IconifyIcon icon={"solar:qr-code-bold"} fontSize={18} />
            <span>{labels.qr_code}</span>
            {registerType === "qr" && (
              <IconifyIcon
                icon={"material-symbols:check-rounded"}
                fontSize={18}
              />
            )}
          </button>
        </div>
      </div>
      <div className="main-register-body" style={{ position: "relative" }}>
        {showLogButton && (
          <div
            className="w-100 border-box flex align-items-center justify-content-between gap-4 px-4"
            style={{
              height: 50,
              position: "absolute",
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              zIndex: 100,
            }}
          >
            <div className={`w-50 border-box`}>
              <Button
                fullWidth
                size="small"
                disableElevation
                onClick={() => {
                  setStep("step_1");
                  defaultManual();
                }}
              >
                {labels.cancel}
              </Button>
            </div>
            <div className={`w-50 border-box`}>
              {registerType === "manual" && selectedGate?.type !== "exit" && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={disabledManualLog}
                  onClick={manualLog}
                >
                  {!hasRegisterAuthorization
                    ? labels.send_for_authorization
                    : labels.generate_log}
                </Button>
              )}
              {!hasUserAccessGroupsPerms && registerType === "qr" && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={
                    selectedGate?.type !== "exit"
                      ? disabledQrEntranceLog
                      : disabledQrExitLog
                  }
                  onClick={selectedGate?.type !== "exit" ? qrLog : exitQrLog}
                >
                  {labels.generate_log}
                </Button>
              )}
              {hasUserAccessGroupsPerms && registerType === "qr" && validQr && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={
                    selectedGate?.type !== "exit"
                      ? disabledQrEntranceLog
                      : disabledQrExitLog
                  }
                  onClick={selectedGate?.type !== "exit" ? qrLog : exitQrLog}
                >
                  {labels.generate_log}
                </Button>
              )}
            </div>
          </div>
        )}
        <div
          className="register-body border-box p-4"
          style={{ position: "relative" }}
        >
          {isLoading ? (
            <div
              className="w-100 flex align-items-center justify-content-center"
              style={{ height: "calc(100% - 50px)" }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              {step === "step_1" && (
                <div className="w-100 border-box flex flex-dir-col align-items-center gap-4">
                  <p
                    className="body-text-md mb-6"
                    style={{ textAlign: "center" }}
                  >
                    {labels.select_gate}
                  </p>
                  {visibleGates &&
                    visibleGates.map((item, index) => (
                      <div
                        key={`${item?.id}-${index}`}
                        className="card border-box p-1"
                        onClick={() => {
                          const fixedGate: GateInfo = {
                            id: item?.id,
                            name: item?.name,
                            type: item?.type,
                          };
                          setSelectedGate(fixedGate);
                          if (
                            registerType === "manual" &&
                            (fixedGate.type === "entrance" ||
                              fixedGate.type === "exit")
                          ) {
                            applyManualRegistrationConfig(fixedGate.type);
                          }
                          setStep("step_2");
                        }}
                      >
                        <div
                          className={`gates-container border-box flex justify-content-between align-items-center p-4 py-7 ${
                            item?.type === "exit"
                              ? "exit-border"
                              : "entrance-border"
                          }`}
                        >
                          <div className="border-box flex align-items-center">
                            <div className="border-box flex align-items-center">
                              {item?.type === "entrance" && (
                                <IconifyIcon
                                  icon={"majesticons:door-enter"}
                                  className="entrance-color"
                                  style={{
                                    fontSize: 40,
                                    transform: "scaleX(-1)",
                                  }}
                                />
                              )}
                              {item?.type === "exit" && (
                                <IconifyIcon
                                  icon={"majesticons:door-exit"}
                                  className="exit-color"
                                  style={{
                                    fontSize: 40,
                                  }}
                                />
                              )}
                            </div>
                            <div className="ml-6">
                              {item?.type === "entrance" && (
                                <p className="body-text-sm entrance-color">
                                  {labels.entrance}
                                </p>
                              )}
                              {item?.type === "exit" && (
                                <p className="body-text-sm exit-color">
                                  {labels.exit}
                                </p>
                              )}
                              <p className="mt-2">{item?.name}</p>
                            </div>
                          </div>
                          <div
                            className="border-box flex align-items-center justify-content-end"
                            style={{ minWidth: 40 }}
                          >
                            {registerType === "qr" && (
                              <IconifyIcon
                                icon={"arcticons:qr"}
                                style={{ fontSize: 30 }}
                              />
                            )}
                            {registerType === "manual" && (
                              <IconifyIcon
                                icon={"arcticons:simple-clipboard-editor"}
                                style={{ fontSize: 30 }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              {step === "step_2" && (
                <>
                  {registerType === "qr" && (
                    <div className="w-100 border-box flex flex-dir-col align-items-center gap-4">
                      <p
                        className="body-text-md mb-6"
                        style={{ textAlign: "center" }}
                      >
                        {cameraStatus === "available"
                          ? labels.qr_to_camera
                          : cameraStatus === "checking" ||
                              cameraStatus === "idle"
                            ? labels.camera_checking
                            : labels.camera_unavailable}
                      </p>
                      <Button
                        size="small"
                        className="px-8"
                        disableElevation
                        onClick={() => {
                          setStep("step_1");
                        }}
                      >
                        {labels.cancel}
                      </Button>
                      <div className="w-90" style={{ maxWidth: 400 }}>
                        {(cameraStatus === "checking" ||
                          cameraStatus === "idle") && (
                          <div
                            className="w-100 flex align-items-center justify-content-center"
                            style={{ minHeight: 200 }}
                          >
                            <CircularProgress />
                          </div>
                        )}
                        {cameraStatus === "unavailable" && (
                          <div
                            className="w-100 flex align-items-center justify-content-center p-4"
                            style={{
                              minHeight: 200,
                              border: "1px #e0e0e0 solid",
                              borderRadius: 4,
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <p
                              className="body-text-sm subtitle-color m-0"
                              style={{ textAlign: "center" }}
                            >
                              {labels.camera_unavailable}
                            </p>
                          </div>
                        )}
                        {cameraStatus === "available" && (
                          <Scanner
                            allowMultiple
                            scanDelay={1000}
                            onScan={(result) => onReadCode(result)}
                            constraints={constraints}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {registerType === "manual" && (
                    <div
                      className="w-100 border-box flex flex-dir-col align-items-center gap-4"
                      style={{ paddingBottom: 50 }}
                    >
                      {!manualScheduleAllowed ? (
                        <>
                          <Alert severity="warning" sx={{ width: "100%" }}>
                            {scheduleWarningMessage}
                          </Alert>
                          <Button
                            size="small"
                            className="px-8 mt-4"
                            disableElevation
                            onClick={() => {
                              setStep("step_1");
                              defaultManual();
                            }}
                          >
                            {labels.cancel}
                          </Button>
                        </>
                      ) : (
                        <>
                          <p
                            className="body-text-md mb-6"
                            style={{ textAlign: "center" }}
                          >
                            {selectedGate?.type === "entrance"
                              ? labels.enter_log_data
                              : labels.search_and_select_visitor}
                          </p>
                          {selectedGate?.type === "exit" && (
                            <ManualExitLog
                              visitorSearch={visitorSearch}
                              setVisitorSearch={setVisitorSearch}
                              selectedAccessToExit={selectedAccessToExit}
                              setSelectedAccessToExit={setSelectedAccessToExit}
                              photos={photos}
                              selectedPhoto={selectedPhoto}
                              setSelectedPhoto={setSelectedPhoto}
                              setShowPictureModal={setShowPictureModal}
                              accessesToExit={accessesToExit}
                              accessesLoading={accessesLoading}
                              onLogExit={onLogExit}
                            />
                          )}
                          {selectedGate?.type === "entrance" && (
                            <ManualEntranceLog
                              hasRegisterAuthorization={
                                hasRegisterAuthorization
                              }
                              visitorName={visitorName}
                              setVisitorName={setVisitorName}
                              countryCode={countryCode}
                              setCountryCode={setCountryCode}
                              visitorPhone={visitorPhone}
                              setVisitorPhone={setVisitorPhone}
                              defaultUnitsProps={defaultUnitsProps}
                              unit={unit}
                              setUnit={setUnit}
                              unitInputValue={unitInputValue}
                              setUnitInputValue={setUnitInputValue}
                              defaultUsersProps={defaultUsersProps}
                              selectedUser={selectedUser}
                              setSelectedUser={setSelectedUser}
                              userInputValue={userInputValue}
                              setUserInputValue={setUserInputValue}
                              motive={motive}
                              setMotive={setMotive}
                              requirements={activeRequirements}
                              requirementsValues={requirementsValues}
                              setRequirementsValues={setRequirementsValues}
                              selectedRule={selectedRule}
                              idFile={idFile}
                              setIdFile={setIdFile}
                              plateFile={plateFile}
                              setPlateFile={setPlateFile}
                              portraitFile={portraitFile}
                              setPortraitFile={setPortraitFile}
                              equipmentFile={equipmentFile}
                              setEquipmentFile={setEquipmentFile}

                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {step === "step_3" && (
                <>
                  {registerType === "qr" && (
                    <div
                      className="w-100 border-box flex flex-dir-col align-items-center gap-4"
                      style={{ paddingBottom: 50, paddingTop: 20 }}
                    >
                      {message && (
                        <>
                          {validQr ? (
                            <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                              <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                                <div className="w-100 flex align-items-center justify-content-center gap-4 mb-4">
                                  <IconifyIcon
                                    icon={"lets-icons:check-fill"}
                                    style={{ fontSize: 20 }}
                                    className="green-color"
                                  />
                                  <p
                                    className={`text-center font-18 font-w-700 green-color uppercase`}
                                  >
                                    {labels.authorized}
                                  </p>
                                </div>
                                <p
                                  className={`text-center font-14 font-w-500 green-color`}
                                >
                                  {message}
                                </p>
                              </div>
                              {currentAccessKey && (
                                <div className="w-100 pt-10">
                                  <p className="body-text-lg mb-4 font-w-700">
                                    {labels.visitor}:{" "}
                                    {currentAccessKey?.verified_visitor?.id
                                      ? currentAccessKey?.verified_visitor
                                          ?.username
                                      : currentAccessKey?.one_time_visitor
                                            ?.provider?.name
                                        ? currentAccessKey?.one_time_visitor
                                            ?.provider?.name
                                        : currentAccessKey?.one_time_visitor
                                              ?.provider_name
                                          ? currentAccessKey?.one_time_visitor
                                              ?.provider_name
                                          : currentAccessKey?.one_time_visitor
                                                ?.name
                                            ? currentAccessKey?.one_time_visitor
                                                ?.name
                                            : ""}
                                  </p>
                                  <p className="body-text-md mb-6 font-w-700">
                                    {currentAccessKey?.reason_for_visit}
                                  </p>
                                  {currentAccessKey?.identifier && (
                                    <p className="body-text-md mb-6 font-w-700">
                                      {labels.identifier}:{" "}
                                      {currentAccessKey?.identifier}
                                    </p>
                                  )}
                                  <p className="body-text-md mb-4 mt-10">
                                    {labels.user}:{" "}
                                    {currentAccessKey?.created?.username}
                                  </p>
                                  <p className="body-text-md mb-6">
                                    {labels.unit}:{" "}
                                    {currentAccessKey?.unit?.name}
                                  </p>
                                  {isEntrance &&
                                    qrScheduleAllowed &&
                                    qrRequirementTypes.length > 0 && (
                                      <>
                                        <Divider className="w-100 pt-10 pb-10">
                                          <p className="subtitle-color font-14">
                                            {labels.registration_requirements} *
                                          </p>
                                        </Divider>
                                        <div className="w-100 border-box flex flex-dir-col gap-6 mt-4">
                                          {qrRequirementTypes.map(
                                            (item, index) => (
                                              <RequirementsComponent
                                                key={`${item?.id}-${index}`}
                                                requirement={item}
                                                requirementIndex={index}
                                                value={requirementsValues}
                                                setValue={setRequirementsValues}
                                              />
                                            ),
                                          )}
                                        </div>
                                      </>
                                    )}
                                  {currentAccessKey?.requirements &&
                                    currentAccessKey?.requirements?.data
                                      ?.length > 0 && (
                                      <>
                                        <Divider />
                                        <div className="w-100 border-box flex flex-dir-col gap-6 mt-4">
                                          {currentAccessKey?.requirements?.data?.map(
                                            (requirement, index) => (
                                              <div
                                                key={`${requirement?.id}-${index}`}
                                              >
                                                <p>
                                                  {
                                                    requirement
                                                      ?.requirement_type?.name
                                                  }
                                                  :{" "}
                                                  <span
                                                    style={{ fontWeight: 500 }}
                                                  >
                                                    {requirement?.value}
                                                  </span>
                                                </p>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </>
                                    )}
                                </div>
                              )}
                              {!qrScheduleAllowed && (
                                <Alert
                                  severity="warning"
                                  sx={{ width: "100%", mt: 4 }}
                                >
                                  {qrScheduleWarningMessage}
                                </Alert>
                              )}
                              {isEntrance &&
                                currentAccessKey &&
                                qrScheduleAllowed &&
                                accessKeyPhotos.length > 0 && (
                                  <>
                                    <Divider className="w-100 pt-10 pb-10">
                                      <p className="subtitle-color font-14">
                                        {labels.required_photographs} *
                                      </p>
                                    </Divider>
                                    <div className="w-100 border-box flex align-items-center justify-content-evenly gap-6 mt-4">
                                      {accessKeyPhotos.map((item, index) => (
                                        <PhotoButton
                                          key={`${item}-${index}`}
                                          photoType={
                                            item as
                                              | "id"
                                              | "plates"
                                              | "visitor"
                                              | "other"
                                          }
                                          file={
                                            item === "id"
                                              ? idFile
                                              : item === "plates"
                                                ? plateFile
                                                : item === "visitor"
                                                  ? portraitFile
                                                  : item === "other"
                                                    ? equipmentFile
                                                    : null
                                          }
                                          setFile={
                                            item === "id"
                                              ? setIdFile
                                              : item === "plates"
                                                ? setPlateFile
                                                : item === "visitor"
                                                  ? setPortraitFile
                                                  : item === "other"
                                                    ? setEquipmentFile
                                                    : () => {}
                                          }
                                        />
                                      ))}
                                    </div>
                                  </>
                                )}
                            </div>
                          ) : (
                            <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                              <div className="w-100 flex align-items-center justify-content-center gap-4 mb-4">
                                <IconifyIcon
                                  icon={"entypo:block"}
                                  style={{ fontSize: 20 }}
                                  className="red-color"
                                />
                                <p
                                  className={`text-center font-18 font-w-700 red-color uppercase`}
                                >
                                  {labels.not_authorized}
                                </p>
                              </div>
                              <p
                                className={`text-center font-14 font-w-500 red-color`}
                              >
                                {message}
                              </p>
                              {hasUserAccessGroupsPerms && userQrId && (
                                <div className="w-100 flex justify-content-center mt-10">
                                  <Button
                                    variant="contained"
                                    onClick={sendAuthRequest}
                                  >
                                    {labels.send_authorization}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterGuardLog;
